//------------------------------------------------------------------------------
// BFA10101G 共架申込一覧
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Field } from 'redux-form';
import { searchMap } from '@/common/common';
import pulldown from '@/utils/pulldown-utils';
import address from '@/components/address-fields';
import {
  renderSelect,
  renderCheckbox,
  renderTextField,
  renderSelectMultiLine,
} from '@/components/atoms/CustomPart';

// 共架者申込ステータス
const ORDER_STATUS = new Set([
  '01', '02', '03',  // 作成中,         技術検討申請中,  技術検討申込済
  '07', '08', '09',  // 申込受付済,     技術検討依頼済,  技術検討中
  '10', '11', '21',  // (未定義),       申込修正/取消要, 改修設計中
  '25', '26', '27',  // 他社工事中,     共架申込未,      共架申込申請中
  '28', '31', '32',  // 共架申込中,     工事依頼済,      工事計画済
  '33', '41', '43',  // 工事完了,       共架工事開始可,  共架工事計画済
  '44', '45', '46',  // しゅん工登録済, 施設確認中,      施設確認済
  '47',              // (未定義)
  '51', '52', '53',  // 完結(不適),     完結,            完結(取消)
]);

/**
 * 検索条件項目一覧
 */
class SearchConditions extends React.Component {

  /**
   * コンストラクタ
   */
  constructor(props) {
    super(props);
    const {
      genericMap,
      areaList,
    } = props;

    // 申込ステータスプルダウン
    this.statusItems = searchMap(genericMap, 'order_status_kyogasya')
        .filter((gen) => ORDER_STATUS.has(gen.cd))
        .map((gen) => pulldown.makeItem(gen.cd, gen.cdName));

    // 地区プルダウン
    this.areaItems = [
      pulldown.EMPTY_ITEM,
      ...(areaList ?? [])
          .map((a) => pulldown.makeItem(a.areaCode, a.areaName)),
    ];
  }

  /**
   * 描画
   */
  render() {
    const {
      values,
      change,
      genericMap,
    } = this.props;

    // 描画
    return (
      <Grid container
        key="search-panel"
        spacing={1} sx={{
          m: 0, pr: 1,
          justifyContent: "flex-start",
          alignItems: "flex-start",
        }}
      >
        {/* 申込番号 */}
        <Grid item xs={6} sm={3}>
          <Field name="orderNo" label="申込番号"
            fullWidth component={renderTextField}
          />
        </Grid>

        {/* 共架者申込番号 */}
        <Grid item xs={6} sm={3}>
          <Field name="kyogasyaOrderNo" label="共架者申込番号"
            fullWidth component={renderTextField}
          />
        </Grid>

        {/* 工事件名 */}
        <Grid item xs={12} sm={6}>
          <Field name="kojiKenmei" label="工事件名"
            fullWidth component={renderTextField}
          />
        </Grid>

        {/* 申込ステータス */}
        <Grid item xs={12} sm={6}>
          <Field name="orderStatus" label="申込ステータス"
            fullWidth component={renderSelectMultiLine}
          >
            {this.statusItems}
          </Field>
        </Grid>

        {/* 手持箇所 */}
        <Grid item xs={6} sm={3}>
          <Field name="temotiKasyo" label="手持箇所"
            fullWidth component={renderSelect}
          >
            {[
              pulldown.EMPTY_ITEM,
              pulldown.makeItem('共架者', '共架者'),
              pulldown.makeItem('共架者以外', '共架者以外'),
            ]}
          </Field>
        </Grid>

        {/* 工事場所 */}
        <Grid item xs={12}
          component="fieldset"
          sx={{ mt: 2, mb: 1, border: 'none' }}
        >
          {/* 工事場所ラベル */}
          <Typography component="legend"> 工事場所 </Typography>
          <Grid container spacing={1}
            sx={{ mt: -1.5, pl: 2 }}
          >
            {/* 工事場所/地区 */}
            <Grid item xs={6} sm={3}>
              <Field name="kojiArea" label="地区"
                fullWidth component={renderSelect}
              >
                {this.areaItems}
              </Field>
            </Grid>

            {/* 工事場所/都道府県 */}
            <Grid item xs={6} sm={3}>
              <address.TodofukenField
                name="kojiPrefectures" label="都道府県"
                genericMap={genericMap}
                withEmpty={true}
                onChange={this.handleChangeTodofuken}
              />
            </Grid>

            {/* 工事場所/市区町村 */}
            <Grid item xs={6} sm={6}>
              <address.SikutyosonField
                name="kojiMunicipalities" label="市区町村"
                inputValue={values?.kojiMunicipalities}
                upperValue={values?.kojiPrefectures}
                withEmpty={true}
                onChange={this.handleChangeSikutyoson}
              />
            </Grid>

            {/* 工事場所/大字通称 */}
            <Grid item xs={6} sm={3}>
              <address.OazatusyoField
                name="kojiOazatusyo" label="大字通称"
                inputValue={values?.kojiOazatusyo}
                upperValue={values?.kojiMunicipalities}
                withEmpty={true}
                onChange={this.handleChangeOazatusyo}
              />
            </Grid>

            {/* 工事場所/字丁目 */}
            <Grid item xs={6} sm={3}>
              <address.AzatyomeField
                name="kojiAzatyome" label="字丁目"
                inputValue={values?.kojiAzatyome}
                upperValue={values?.kojiOazatusyo}
                withEmpty={true}
              />
            </Grid>

            {/* 工事場所/ビル名等 */}
            <Grid item xs={6} sm={6}>
              <Field name="kojiBirumei" label="ビル名等"
                fullWidth component={renderTextField}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* 申込日 */}
        <Grid item xs={12} sm={6}>
          {/* TODO コンポーネント化 DateRange */}
          <Stack direction="row" spacing={1}
            sx={{ alignItems: 'baseline' }}
          >
            <Field name="orderDateFrom" label="申込日"
              type="date" component={renderTextField}
              InputLabelProps={{ shrink: true }}
            />
            <span>～</span>
            <Field name="orderDateTo" label={pulldown.NBSP}
              type="date" component={renderTextField}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Grid>

        {/* 更新日 */}
        <Grid item xs={12} sm={6}>
          <Stack direction="row" spacing={1}
            sx={{ alignItems: 'baseline' }}
          >
            <Field name="lastUpdateFrom" label="更新日"
              type="date" component={renderTextField}
              InputLabelProps={{ shrink: true }}
            />
            <span>～</span>
            <Field name="lastUpdateTo" label={pulldown.NBSP}
              type="date" component={renderTextField}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Grid>

        {/* 代行申込 */}
        <Grid item xs={6} sm={4} md={3}>
          <Field name="daikoOrder" label="代行申込"
            component={renderCheckbox}
          />
        </Grid>

        {/* 旧版表示 */}
        <Grid item xs={6} sm={4} md={3}>
          <Field name="oldRevision" label="旧版表示"
            component={renderCheckbox}
          />
        </Grid>
      </Grid>
    );
  }
  // TODO 文字型/数値型 max-length で桁数制限
  // TODO 日付範囲バリデーション
  // TODO 日付範囲でコンポーネント化

  // 都道府県変更 - 市区町村/大字通称/字丁目クリア
  handleChangeTodofuken = () => {
    this.props.change('kojiMunicipalities', '');
    this.props.change('kojiOazatusyo', '');
    this.props.change('kojiAzatyome', '');
  }

  // 市区町村変更 - 大字通称/字丁目クリア
  handleChangeSikutyoson = () => {
    this.props.change('kojiOazatusyo', '');
    this.props.change('kojiAzatyome', '');
  }

  // 大字通称変更 - 字丁目クリア
  handleChangeOazatusyo = () => {
    this.props.change('kojiAzatyome', '');
  }
}

// プロパティ
SearchConditions.propTypes = {

  // フォーム
  change: PropTypes.func,
  values: PropTypes.object,

  // ストア
  genericMap: PropTypes.object,   // 汎用マスタ
  areaList: PropTypes.array,      // 地区マスタ
};

// ストア接続
const mapStateToProps = (state) => ({
  genericMap: state.master.genericMap,
  areaList: state.master.areaList,
});
const mapDispatchToProps = {};
SearchConditions = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SearchConditions);

// エクスポート
export default SearchConditions;
